import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Radio, Compass, Users, Sparkles, TrendingUp, Play, ArrowRight, Clock, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function StreamCard({ s, size = "md" }) {
  return (
    <Link
      to={`/app/live/${s.id}`}
      data-testid={`stream-card-${s.id}`}
      className="group block nx-panel overflow-hidden hover:border-[hsl(var(--nx-blue))] transition-colors"
    >
      <div className="relative aspect-video overflow-hidden">
        <img src={s.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute top-2.5 left-2.5 nx-chip nx-chip-red !py-1 !px-2">
          <span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO
        </div>
        <div className="absolute top-2.5 right-2.5 nx-chip !py-1 !px-2">
          {s.viewers.toLocaleString("pt-BR")}
        </div>
        <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm truncate">{s.title}</div>
            <div className="text-white/70 text-[11px] truncate">@{s.creator} · {s.game}</div>
          </div>
          <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur border border-white/20 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="w-4 h-4 text-white ml-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const { servers = [] } = useOutletContext() || {};
  const [streams, setStreams] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/discover/streams").then((r) => setStreams(r.data.slice(0, 6))).catch(() => {});
    api.get("/discover/categories").then((r) => setCats(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <main className="flex-1 min-w-0 overflow-y-auto scrollbar-thin" data-testid="home-page">
      {/* Hero banner */}
      <section className="relative border-b border-[hsl(var(--nx-border))] overflow-hidden">
        <div className="absolute inset-0 nx-grid-bg opacity-40 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[hsl(var(--nx-blue)/0.16)] blur-[120px] pointer-events-none" />
        <div className="relative px-6 lg:px-10 py-10 lg:py-14 max-w-6xl">
          <div className="nx-chip nx-chip-cyan mb-3">Olá {user?.username ? `, ${user.display_name || user.username}` : ""}</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Bem-vindo à <span className="nx-text-gradient">Nexus Live</span>.<br />
            Sua tribo está online.
          </h1>
          <p className="mt-3 max-w-xl text-muted-foreground">
            Descubra comunidades, entre em canais e acompanhe seus criadores favoritos ao vivo.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/app/explorar" data-testid="home-cta-explore" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl nx-btn-blue border-0 font-semibold text-sm">
              <Compass className="w-4 h-4" /> Explorar lives
            </Link>
            <Link to={`/app/criador/${user?.username}`} data-testid="home-cta-profile" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-[hsl(var(--nx-border))] hover:bg-white/5 text-sm">
              <Sparkles className="w-4 h-4 text-[hsl(var(--nx-cyan))]" /> Meu canal
            </Link>
          </div>
        </div>
      </section>

      {/* Quick tiles */}
      <section className="px-6 lg:px-10 pt-10">
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-3 max-w-6xl">
          <div className="nx-panel p-5 relative overflow-hidden">
            <Users className="w-6 h-6 text-[hsl(var(--nx-blue))]" />
            <div className="mt-4 font-display font-bold text-2xl">{servers.length}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Suas comunidades</div>
          </div>
          <div className="nx-panel p-5 relative overflow-hidden">
            <Flame className="w-6 h-6 text-[hsl(var(--nx-magenta))]" />
            <div className="mt-4 font-display font-bold text-2xl">{streams.length}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Lives ao vivo agora</div>
          </div>
          <div className="nx-panel p-5 relative overflow-hidden">
            <TrendingUp className="w-6 h-6 text-[hsl(var(--nx-cyan))]" />
            <div className="mt-4 font-display font-bold text-2xl">{cats.length}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Categorias populares</div>
          </div>
        </div>
      </section>

      {/* My communities */}
      {servers.length > 0 && (
        <section className="px-6 lg:px-10 pt-12 max-w-6xl">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="nx-chip mb-2">Suas comunidades</div>
              <h2 className="font-display font-bold text-2xl">Continue de onde parou</h2>
            </div>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {servers.slice(0, 8).map((s) => (
              <Link
                key={s.id}
                to={`/app/servidor/${s.id}`}
                data-testid={`home-server-${s.id}`}
                className="group nx-panel p-4 hover:border-[hsl(var(--nx-blue))] transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] grid place-items-center font-display font-bold text-white text-lg mb-3">
                  {s.name?.[0]?.toUpperCase()}
                </div>
                <div className="font-semibold text-sm truncate">{s.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{s.member_count || 1} membros</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Live now */}
      <section className="px-6 lg:px-10 pt-12 max-w-6xl">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="nx-chip nx-chip-red mb-2"><span className="nx-live-dot" /> Ao vivo</div>
            <h2 className="font-display font-bold text-2xl">Descubra agora</h2>
          </div>
          <Link to="/app/explorar" className="text-sm text-[hsl(var(--nx-cyan))] hover:underline inline-flex items-center gap-1" data-testid="home-see-all-streams">
            ver todas <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {streams.map((s) => <StreamCard key={s.id} s={s} />)}
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 lg:px-10 pt-14 pb-14 max-w-6xl">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="nx-chip nx-chip-cyan mb-2">Categorias</div>
            <h2 className="font-display font-bold text-2xl">Populares agora</h2>
          </div>
        </div>
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {cats.map((c) => (
            <Link
              key={c.id}
              to="/app/explorar"
              data-testid={`home-category-${c.id}`}
              className="group nx-panel overflow-hidden hover:border-[hsl(var(--nx-cyan))] transition-colors"
            >
              <div className="aspect-[3/4] overflow-hidden relative">
                <img src={c.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="text-white text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-white/70 text-[10px]">{c.viewers.toLocaleString("pt-BR")} assistindo</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
